import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { notificationIcon, type NotificationItem, type NotificationsResponse } from "@omts/shared";
import { api, subscribeEvents } from "../api";
import { fmtDateTime } from "../time";

/** Относительное время для свежих уведомлений: «только что», «5 мин назад», иначе дата. */
export function fmtAgo(iso: string, now = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  if (diff < 60_000) return "только что";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 24 * 3_600_000) return `${Math.floor(diff / 3_600_000)} ч назад`;
  return fmtDateTime(iso);
}

/** Одна строка уведомления: иконка, заголовок, текст, время; клик — прочитано + переход в карточку. */
export function NotificationRow({ n, onOpen }: { n: NotificationItem; onOpen: (n: NotificationItem) => void }) {
  const showBody = n.body && !/^https?:\/\/\S+$/.test(n.body);
  return (
    <div
      className={`notif${n.readAt ? "" : " unread"}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(n)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(n)}
    >
      <span className="icon">{notificationIcon(n.type)}</span>
      <span className="title">{n.title}</span>
      {showBody && <span className="body">{n.body}</span>}
      <span className="when" title={fmtDateTime(n.createdAt)}>
        {fmtAgo(n.createdAt)}
        {n.lotNumber && <span className="tag">{n.lotNumber}</span>}
        {n.sentTelegramAt && <span className="tag tg">в Telegram</span>}
      </span>
    </div>
  );
}

/** Загрузка уведомлений с обновлением по SSE (событие notification приходит только своему пользователю). */
export function useNotifications(limit: number) {
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    api
      .notifications(limit)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Ошибка загрузки"));
  }, [limit]);

  useEffect(() => {
    load();
    return subscribeEvents((type) => {
      if (type === "notification") load();
    });
  }, [load]);

  const markRead = async (n: NotificationItem) => {
    if (n.readAt) return;
    setData((d) => d && { unreadCount: Math.max(0, d.unreadCount - 1), items: d.items.map((i) => (i.id === n.id ? { ...i, readAt: new Date().toISOString() } : i)) });
    await api.readNotification(n.id).catch(() => undefined);
  };
  const markAllRead = async () => {
    setData((d) => d && { unreadCount: 0, items: d.items.map((i) => (i.readAt ? i : { ...i, readAt: new Date().toISOString() })) });
    await api.readAllNotifications().catch(() => undefined);
    load();
  };

  return { data, error, reload: load, markRead, markAllRead };
}

export function NotificationBell() {
  const { data, markRead, markAllRead } = useNotifications(20);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const unread = data?.unreadCount ?? 0;

  // Закрытие по клику вне и по Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = (n: NotificationItem) => {
    void markRead(n);
    setOpen(false);
    if (n.lotId) navigate(`/lots/${n.lotId}`);
  };

  return (
    <div className="bell" ref={ref}>
      <button
        type="button"
        className={`bell-btn${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={unread ? `Уведомления: ${unread} непрочитанных` : "Уведомления"}
        title="Уведомления"
      >
        🔔
        {unread > 0 && <span className="bell-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="bell-menu">
          <div className="bell-head">
            <span>Уведомления{unread > 0 && <span className="muted"> · {unread} новых</span>}</span>
            <button type="button" className="btn" disabled={unread === 0} onClick={() => void markAllRead()}>
              Отметить все прочитанными
            </button>
          </div>
          {!data ? (
            <p className="notif-empty">Загрузка…</p>
          ) : data.items.length === 0 ? (
            <p className="notif-empty">Уведомлений пока нет.</p>
          ) : (
            <ul className="notif-list">
              {data.items.map((n) => (
                <li key={n.id}>
                  <NotificationRow n={n} onOpen={openItem} />
                </li>
              ))}
            </ul>
          )}
          <div className="bell-foot">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              Все уведомления
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
